// Web-Push client glue: register the service worker, subscribe to
// push via PushManager, ship the subscription up to /api/push/subscribe.
// Keeps lib/notifications.ts focused on the in-page Notification API
// for active-tab notifications (those still help: instant, no
// dependency on the push-service latency).
//
// We do NOT auto-subscribe at app load — only after the user opts in
// from settings. That keeps the Notification permission prompt
// gated behind an explicit click.

import { api } from './api'
import { registerAppServiceWorker } from './sw-register'

const swPath = () => new URL('sw.js', document.baseURI).pathname
const swScope = () => new URL('.', document.baseURI).href

export function pushSupported(): boolean {
  if (typeof window === 'undefined') return false
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** Idempotently register the embertime service worker. Resolves with
 * the active registration. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  return registerAppServiceWorker()
}

/** Convert the server-provided base64url VAPID public key into the
 * Uint8Array PushManager.subscribe expects. */
function urlBase64ToUint8(b64url: string): Uint8Array {
  const padding = '='.repeat((4 - (b64url.length % 4)) % 4)
  const base64 = (b64url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** Subscribe this browser to push for the logged-in user and ship the
 * resulting endpoint up to the backend. Browser permission prompt
 * happens here if not already granted. Returns true on success. */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false
  const perm =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission
  if (perm !== 'granted') return false

  const reg = await registerServiceWorker()
  if (!reg) return false

  // If we already have an active subscription, just re-POST it so the
  // backend stays in sync (covers cases where the row got deleted but
  // the browser still has the same endpoint).
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    const { publicKey } = await api.getVapidPublicKey()
    // Allocate into a dedicated ArrayBuffer so the resulting Uint8Array
    // is backed by ArrayBuffer (not SharedArrayBuffer) — what the
    // PushManager.subscribe BufferSource overload expects in TS lib.
    const keyBytes = urlBase64ToUint8(publicKey)
    const keyBuffer = new Uint8Array(new ArrayBuffer(keyBytes.byteLength))
    keyBuffer.set(keyBytes)
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBuffer,
    })
  }
  const json = sub.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false
  await api.subscribePush({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  })
  return true
}

/** Best-effort unsubscribe: stop the browser subscription AND delete
 * the row(s) on the server. Used by the Settings UI's "Deaktivieren"
 * button. Permission stays granted; user can re-enable any time. */
export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const reg = await navigator.serviceWorker.getRegistration(swPath())
  if (!reg || reg.scope !== swScope()) return
  const sub = await reg.pushManager.getSubscription()
  // Capture THIS browser's endpoint before unsubscribing so we only
  // delete the matching server row — not the user's phone/other browsers.
  const localEndpoint = sub?.endpoint ?? null
  if (sub) {
    try {
      await sub.unsubscribe()
    } catch {
      // ignore — server-side cleanup will handle it on next push
    }
  }
  // Delete only the server subscription(s) for THIS device (matched by
  // endpoint). Disabling push on the desktop must not silently kill push
  // on the user's other devices — those are managed via the per-device
  // list in Settings.
  if (!localEndpoint) return
  try {
    const { subscriptions } = await api.listPushSubscriptions()
    await Promise.all(
      subscriptions
        .filter((s) => s.endpoint === localEndpoint)
        .map((s) => api.unsubscribePush(s.id)),
    )
  } catch {
    // non-fatal
  }
}

/** Returns true if this browser is currently subscribed (has both
 * permission AND an active PushSubscription registered). */
export async function pushEnabled(): Promise<boolean> {
  if (!pushSupported()) return false
  if (Notification.permission !== 'granted') return false
  const reg = await navigator.serviceWorker.getRegistration(swPath())
  if (!reg || reg.scope !== swScope()) return false
  const sub = await reg.pushManager.getSubscription()
  return !!sub
}
